import { XIcon } from "lucide-react-native";
import React, { ReactNode, memo } from "react";
import { Platform, ViewStyle } from "react-native";

import { Box } from "@/components/atoms/Box";
import { Heading } from "@/components/atoms/Heading";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import {
  Modal,
  ModalBackdrop,
  ModalContent,
  ModalHeader,
} from "@/components/atoms/Modal";
import { Pressable } from "@/components/atoms/Pressable";
import { ifWeb } from "@/utils/platform";

/** Ensures body View fills available space so children get bounded flex layout */

/**
 * Props for the BaseModalView component
 */
interface BaseModalViewProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  size?: "xs" | "sm" | "md" | "lg" | "full";
  headerContent?: ReactNode;
  children: ReactNode;
  minHeight?: string;
  maxHeight?: string;
  /**
   * Whether clicking the overlay/backdrop dismisses the modal.
   * @default true
   */
  closeOnOverlayClick?: boolean;
  /**
   * Whether dark theme is active
   */
  isDark: boolean;
  /**
   * Safe area top inset value
   */
  topInset: number;
}

/**
 * View component that renders a modal with customizable header, body, and styling
 * @param root0 - Component props
 * @param root0.isOpen - Boolean flag indicating if the modal is currently visible
 * @param root0.onClose - Callback function invoked when the modal is closed
 * @param root0.title - Title text displayed in the modal header
 * @param root0.size - Size variant of the modal (xs, sm, md, lg, or full)
 * @param root0.headerContent - Optional additional content to display in the header
 * @param root0.children - Content to be rendered inside the modal body
 * @param root0.minHeight - Minimum height of the modal (web only)
 * @param root0.maxHeight - Maximum height of the modal (web only)
 * @param root0.closeOnOverlayClick - Whether backdrop click dismisses the modal
 * @param root0.isDark - Boolean flag indicating if dark mode is active
 * @param root0.topInset - Safe area top inset value for native platforms
 */
const BaseModalView = ({
  isOpen,
  onClose,
  title,
  size = "lg",
  headerContent,
  children,
  minHeight = "60vh",
  maxHeight = "80vh",
  closeOnOverlayClick = true,
  isDark,
  topInset,
}: BaseModalViewProps) => (
  <Modal
    isOpen={isOpen}
    onClose={onClose}
    size={Platform.OS !== "web" ? "full" : size}
    useRNModal
    closeOnOverlayClick={closeOnOverlayClick}
  >
    <ModalBackdrop onPress={closeOnOverlayClick ? onClose : undefined} />
    <ModalContent
      className={`${Platform.OS !== "web" ? "size-full rounded-none border-0 border-none shadow-none" : ""} flex flex-col p-0 ${isDark ? "bg-surface-inverse" : "bg-surface-base"}`}
      // eslint-disable-next-line no-restricted-syntax -- runtime safe-area topInset (native) + web-only min/maxHeight vh viewport units (off token ladder) + theme-corrective bg hex (bg-background-* token inverts to near-white in both themes here, so no semantic token reproduces #1C1C1E/#F5F5F5); ModalContent is a bare re-export atom with no UNSAFE_style prop, so the runtime style must ride on style=
      style={
        {
          paddingTop: Platform.OS !== "web" ? topInset : undefined,
          backgroundColor: isDark ? "#1C1C1E" : "#F5F5F5",
          ...(Platform.OS === "web"
            ? ({ minHeight, maxHeight } as unknown as ViewStyle)
            : {}),
        } as ViewStyle
      }
    >
      <ModalHeader
        className={`${ifWeb("py-3 px-4", "border-b px-6 py-5")} flex-row items-center justify-between ${isDark ? "border-b border-typography-black" : "border-b border-surface-strong"}`}
      >
        <Stack direction="horizontal" className="flex-1 items-center">
          <Heading
            variant="title-lg"
            className={isDark ? "text-white" : "text-content-primary"}
          >
            {title}
          </Heading>
        </Stack>
        <Stack direction="horizontal" className="gap-2">
          {headerContent}
          <Pressable
            onPress={onClose}
            className="rounded-full bg-surface-subtle p-2"
          >
            <Icon as={XIcon} size="md" className="text-content-secondary" />
          </Pressable>
        </Stack>
      </ModalHeader>
      <Box className="flex-1 grow">{children}</Box>
    </ModalContent>
  </Modal>
);

BaseModalView.displayName = "BaseModalView";

export default memo(BaseModalView);
